import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useMainContext } from '../../../core/contexts/main';
import { useWindowSize } from '../../../core/hooks/useWindowSize';
import { FaArrowRight } from "react-icons/fa6";
import { BiCopy } from "react-icons/bi";
import { useCopyToClipboard } from "../../../core/hooks/useCopyToClipboard";

export const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, key) => acc && acc[key], obj);
};

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  min-width: ${(props) => props.minWidth}px;

  border-radius: 8px;
`;

const TableContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

const TableHeader = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: ${(props) => (props.lang === "fa" ? "row" : "row-reverse")};
  background-color: ${(props) => props.theme.secondaryBg};
  border-radius: 8px;
  font-weight: bold;
  padding: 8px;
`;

const TableRow = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: ${(props) => (props.lang === "fa" ? "row" : "row-reverse")};
  border-radius: 8px;
  &:nth-child(odd) {
    background-color: ${(props) => props.theme.tableRowBg};
  }
`;

const TableCell = styled.div`
  padding: 8px;
  width: ${props => props.width};
  font-size: 15px;
  font-weight: 400;
  color: ${(props) => props.theme.primary};
  display: flex;
  align-items: center;
  justify-content: ${(props) => props.align};
  white-space: nowrap;
`;

const CustomTable = ({ schema, data, heading = null, minWidth = 768, onDetails }) => {
    const {
        main: { lang },
    } = useMainContext();
    const { t } = useTranslation();
    const { width } = useWindowSize();
    const { copyToClip } = useCopyToClipboard();

    const { columns, rows } = schema(data);

    const handleOnDetailClick = (row) => {
        try {
            onDetails(row);
        } catch (e) {
            console.log(e);
            throw Error("onDetails is not a function");
        }
    };

    const handleCopyCellData = (rowIndex, cell) => {
        let contentToCopy = data[rowIndex][cell.id];
        if (contentToCopy) copyToClip(contentToCopy);
        else throw new Error('Cannot copy undefined. Check the row & column key id in schema.');
    };

    return (
        <>
            {heading ? <div className="mb-4">{heading}</div> : null}
            <TableContainer minWidth={minWidth}>
                <TableContent>
                    {/* Table Header */}
                    <TableHeader lang={lang}>
                        {columns.map((column, index) => (
                            <TableCell
                                key={index}
                                width={column.width || "150px"}
                                align={column.align}
                                className={`${column.className} ${column.responsive >= width ? 'hidden' : ''}`}
                            >
                                {t(column.label)}
                            </TableCell>
                        ))}
                        {
                            onDetails &&
                            <TableCell
                                width="100px"
                                align="flex-end"
                                className={`${720 <= width ? 'hidden' : ''}`}
                            >
                                {t('details')}
                            </TableCell>
                        }
                    </TableHeader>

                    {/* Table Rows */}
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex} lang={lang}>
                            {row.map((cell, cellIndex) => {
                                const columnConfig = columns.find((col) => col.id === cell.id) || columns[cellIndex];
                                return (
                                    <TableCell
                                        key={cellIndex}
                                        width={columnConfig?.width || "150px"}
                                        align={columnConfig?.align}
                                        className={`${columnConfig?.rowClassName} ${columnConfig.responsive >= width ? 'hidden' : ''} text-gray-800 dark:text-white py-4`}
                                    >
                                        {typeof cell.render === "function"
                                            ? cell.render(data[rowIndex])
                                            : cell.render}
                                        {columnConfig.canCopy && (
                                            <BiCopy
                                                className="text-gray-800 dark:text-active mx-1"
                                                size={width <= 520 ? 18 : 22}
                                                onClick={() => handleCopyCellData(rowIndex, cell)}
                                            />
                                        )}
                                    </TableCell>
                                );
                            })}
                            {
                                onDetails &&
                                <TableCell
                                    key="details"
                                    width="100px"
                                    align="flex-end"
                                    className={`${720 <= width ? 'hidden' : ''} text-gray-800 dark:text-white py-4`}
                                    onClick={() => handleOnDetailClick(data[rowIndex])}
                                >
                                    <FaArrowRight className={lang === 'en' ? '' : 'rotate-180'} />
                                </TableCell>
                            }
                        </TableRow>
                    ))}
                </TableContent>
            </TableContainer >
        </>
    );
};

export default CustomTable;
